import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PastWorkWrapper = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);

  @media (max-width: 767px) {
    padding: 60px 0;
  }

  .section-header {
    text-align: center;
    margin-bottom: 50px;
  }

  h2 {
    font-size: 42px;
    font-weight: 300;
    color: #fff;
    letter-spacing: -0.025em;
    line-height: 1.2;
    margin-bottom: 20px;

    @media (max-width: 991px) {
      font-size: 36px;
    }
    @media (max-width: 575px) {
      font-size: 28px;
    }
  }

  .section-subtitle {
    font-size: 17px;
    color: rgba(255, 255, 255, 0.55);
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.7;
    font-weight: 300;

    @media (max-width: 575px) {
      font-size: 15px;
    }
  }

  .subsection-title {
    text-align: center;
    font-size: 13px;
    font-weight: 700;
    color: #f16600;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    margin-bottom: 30px;
  }

  /* Client logos */
  .client-logos {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 30px;
    margin-bottom: 60px;
    padding: 0 20px;
  }

  .logo-item {
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      max-width: 100px;
      max-height: 40px;
      opacity: 0.4;
      filter: brightness(2);
      transition: opacity 0.3s ease;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  /* Past case studies */
  .past-case-studies {
    margin-bottom: 60px;
  }

  .cs-tabs {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 25px;
  }

  .cs-tab {
    padding: 10px 22px;
    font-size: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      color: #fff;
      border-color: rgba(241, 102, 0, 0.3);
    }

    &.active {
      color: #fff;
      background: linear-gradient(135deg, #f16600 0%, #ff8534 100%);
      border-color: transparent;
      box-shadow: 0 6px 20px rgba(241, 102, 0, 0.25);
    }

    @media (max-width: 575px) {
      font-size: 12px;
      padding: 8px 16px;
    }
  }

  .cs-detail {
    background: rgba(255, 255, 255, 0.97);
    border-radius: 16px;
    overflow: hidden;
    animation: ${fadeIn} 0.4s ease-out;
  }

  .cs-detail-header {
    padding: 30px 35px 20px;
    border-bottom: 1px solid #eee;

    .cs-headline {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      display: block;
      margin-bottom: 8px;
    }

    .cs-meta {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .cs-company {
      font-size: 13px;
      font-weight: 700;
      color: #f16600;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      background: rgba(241, 102, 0, 0.08);
      padding: 4px 12px;
      border-radius: 20px;
    }

    .cs-city {
      font-size: 13px;
      color: #888;
    }

    @media (max-width: 575px) {
      padding: 20px 25px 15px;
      .cs-headline { font-size: 20px; }
    }
  }

  .cs-detail-body {
    padding: 30px 35px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;

    @media (max-width: 767px) {
      grid-template-columns: 1fr;
      padding: 20px 25px;
    }
  }

  .cs-info {
    p {
      font-size: 15px;
      color: #555;
      line-height: 1.7;
      margin-bottom: 20px;
    }
  }

  .cs-specs {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
  }

  .spec-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 120px;
  }

  .spec-label {
    font-size: 11px;
    font-weight: 700;
    color: #f16600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 4px;
  }

  .spec-item {
    font-size: 13px;
    color: #666;
  }

  .cs-testimonial {
    background: #f8f9fa;
    padding: 25px;
    border-left: 1px solid #f16600;

    blockquote {
      font-size: 14px;
      color: #555;
      line-height: 1.7;
      font-style: italic;
      margin: 0 0 15px 0;
    }

    .testimonial-author {
      display: flex;
      flex-direction: column;
      gap: 2px;

      strong {
        font-size: 13px;
        color: #333;
      }

      span {
        font-size: 12px;
        color: #888;
      }
    }
  }

  /* Testimonials carousel */
  .testimonials-section {
    padding-top: 10px;
  }

  .testimonials-carousel {
    overflow: visible;
  }

  .testimonial-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 30px 25px;
    min-height: 280px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .testimonial-text {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.7);
      line-height: 1.7;
      font-style: italic;
      margin-bottom: 20px;
    }

    .testimonial-footer {
      display: flex;
      flex-direction: column;
      gap: 3px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      padding-top: 15px;

      strong {
        font-size: 14px;
        color: #fff;
      }

      span {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.4);
      }

      .testimonial-logo {
        max-width: 80px;
        max-height: 30px;
        margin-top: 8px;
        opacity: 0.4;
        filter: brightness(2);
      }
    }
  }

  /* Glide carousel overrides */
  .glide__bullets {
    margin-top: 30px;
    text-align: center;
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  .glide__bullet {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 0;

    &:hover {
      background: rgba(255, 255, 255, 0.4);
    }

    &.glide__bullet--active {
      background: #f16600;
    }
  }
`;

export default PastWorkWrapper;
